import { useQuery, useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";

import {
  companyActivityApi,
  CompanyActivityParams,
  CompanyActivityResponse,
} from "services/companyActivityApi";

export const COMPANY_ACTIVITY_QUERY_KEY = ["company", "activity"] as const;

export const useCompanyActivity = (params?: CompanyActivityParams) =>
  useQuery<CompanyActivityResponse>({
    queryKey: [...COMPANY_ACTIVITY_QUERY_KEY, params],
    queryFn: () => companyActivityApi.getCompanyActivity(params),
    staleTime: 60 * 1000,
    // Keep the current rows on screen while a new search/sort/filter refetches,
    // instead of dropping to skeleton/empty and flashing. The list updates in
    // place (no collapse, no first-sort double-render).
    placeholderData: keepPreviousData,
  });

export const COMPANY_ACTIVITY_PAGE_SIZE = 20;

// Infinite-scroll variant (parity with useCertificateActivityInfinite): fetch a
// page at a time. `params` carries the server-side filters/sort/search (NOT page/
// pageSize) — it's part of the queryKey, so changing any filter resets the infinite
// query to page 1. getNextPageParam returns the next page number while fewer rows
// are loaded than `total`, else undefined (no more pages). The page flattens
// data.pages.flatMap(p => p.items).
export const useCompanyActivityInfinite = (
  params?: Omit<CompanyActivityParams, "page" | "pageSize">
) =>
  useInfiniteQuery({
    queryKey: [...COMPANY_ACTIVITY_QUERY_KEY, "infinite", params],
    queryFn: ({ pageParam = 1 }) =>
      companyActivityApi.getCompanyActivity({
        ...params,
        page: pageParam as number,
        pageSize: COMPANY_ACTIVITY_PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.items.length, 0);
      return loaded < lastPage.total ? allPages.length + 1 : undefined;
    },
    staleTime: 60 * 1000,
  });
